import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useComparison } from '../contexts/ComparisonContext';
import { CountryData } from '../data/countryData';
import { Company } from '../types/CompanyTypes';
import { Job } from '../types/JobTypes';
import SalaryCalculatorPanel from './SalaryCalculatorPanel';
import CompanyDetailsPanel from './CompanyDetailsPanel';
import './ComparisonView.css';

interface ComparisonViewProps {
  type: 'country' | 'company' | 'job';
  onClose: () => void;
  darkMode?: boolean;
  onLoginRequired?: (action: string) => void;
}

const ComparisonView: React.FC<ComparisonViewProps> = ({ type, onClose, darkMode, onLoginRequired }) => {
  const { state, removeFromComparison } = useComparison();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isScrollingSynced = useRef(false);
  
  const key = type === 'country' ? 'countries' : 
              type === 'company' ? 'companies' : 
              'jobs';
  const items = state[key as keyof typeof state] || [];
  
  // Synchronized scrolling handler
  const handleScroll = useCallback((sourceIndex: number) => {
    if (isScrollingSynced.current) return;
    
    const sourcePanel = panelRefs.current[sourceIndex];
    if (!sourcePanel) return;
    
    const scrollTop = sourcePanel.scrollTop;
    const scrollLeft = sourcePanel.scrollLeft;
    
    isScrollingSynced.current = true;
    
    // Sync all other panels
    panelRefs.current.forEach((panel, index) => {
      if (panel && index !== sourceIndex) {
        panel.scrollTop = scrollTop;
      }
    });
    
    // Also sync horizontal scroll on the container
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = scrollLeft;
    }
    
    setTimeout(() => {
      isScrollingSynced.current = false;
    }, 10);
  }, []);
  
  const renderCountryComparison = () => {
    const countries = items as CountryData[];
    
    return (
      <div className="comparison-panels-container" ref={scrollContainerRef}>
        {countries.map((country, index) => (
          <div 
            key={country.code} 
            className="comparison-panel-wrapper"
            style={{ 
              width: items.length === 1 ? 'calc(100% - 40px)' : 
                     items.length === 2 ? 'calc(50% - 30px)' : 
                     items.length === 3 ? 'calc(33.33% - 27px)' : 
                     items.length === 4 ? 'calc(25% - 25px)' : '600px',
              minWidth: '500px'
            }}
          >
            <div className="comparison-panel-header">
              <button 
                className="remove-panel-btn"
                onClick={() => removeFromComparison('country', country.code)}
                aria-label={`Remove ${country.name} from comparison`}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <div 
              className="panel-scroll-wrapper"
              ref={el => { panelRefs.current[index] = el; }}
              onScroll={() => handleScroll(index)}
            >
              <SalaryCalculatorPanel
                country={country}
                onClose={() => removeFromComparison('country', country.code)}
                darkMode={darkMode || false}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  const renderCompanyComparison = () => {
    const companies = items as Company[];
    
    return (
      <div className="comparison-panels-container" ref={scrollContainerRef}>
        {companies.map((company, index) => (
          <div 
            key={company.id} 
            className="comparison-panel-wrapper"
            style={{ 
              width: items.length === 1 ? 'calc(100% - 40px)' : 
                     items.length === 2 ? 'calc(50% - 30px)' : 
                     items.length === 3 ? 'calc(33.33% - 27px)' : 
                     items.length === 4 ? 'calc(25% - 25px)' : '600px',
              minWidth: '500px'
            }}
          >
            <div className="comparison-panel-header">
              <button 
                className="remove-panel-btn"
                onClick={() => removeFromComparison('company', company.id)}
                aria-label={`Remove ${company.name} from comparison`}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <div 
              className="panel-scroll-wrapper"
              ref={el => { panelRefs.current[index] = el; }}
              onScroll={() => handleScroll(index)}
            >
              <CompanyDetailsPanel
                company={company}
                onClose={() => removeFromComparison('company', company.id)}
                darkMode={darkMode || false}
                onLoginRequired={onLoginRequired}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  const renderJobComparison = () => {
    const jobs = items as Job[];
    
    return (
      <div className="comparison-panels-container" ref={scrollContainerRef}>
        {jobs.map((job, index) => (
          <div 
            key={job.id} 
            className="comparison-panel-wrapper"
            style={{ 
              width: items.length === 1 ? 'calc(100% - 40px)' : 
                     items.length === 2 ? 'calc(50% - 30px)' : 
                     items.length === 3 ? 'calc(33.33% - 27px)' : 
                     items.length === 4 ? 'calc(25% - 25px)' : '600px',
              minWidth: '500px'
            }}
          >
            <div className="comparison-panel-header">
              <button 
                className="remove-panel-btn"
                onClick={() => removeFromComparison('job', job.id)}
                aria-label={`Remove ${job.title} from comparison`}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
            <div 
              className="panel-scroll-wrapper"
              ref={el => { panelRefs.current[index] = el; }}
              onScroll={() => handleScroll(index)}
            >
              <div className="job-detail-panel">
                <div className="job-main-header">
                  <h2>{job.title}</h2>
                  <div className="company-info-section">
                    <span className="company-name">{job.company}</span>
                    {job.companyRating && (
                      <div className="company-rating-badge">
                        <span className="star">★</span>
                        <span>{job.companyRating}</span>
                      </div>
                    )}
                  </div>
                  <div className="job-location">{job.location}</div>
                  {job.description && (
                    <p className="job-description">{job.description}</p>
                  )}
                </div>
                
                <div className="salary-showcase">
                  <div className="salary-amount-large">${job.salary.toLocaleString()}</div>
                  <div className="salary-label">Annual Salary</div>
                  {job.salaryPercentile && (
                    <div className="salary-percentile">
                      <div className="percentile-bar">
                        <div className="percentile-fill" style={{ width: `${job.salaryPercentile}%` }}></div>
                        <div className="percentile-marker" style={{ left: `${job.salaryPercentile}%` }}></div>
                      </div>
                      <div className="percentile-label">Top {100 - job.salaryPercentile}% in {job.location}</div>
                    </div>
                  )}
                </div>
                
                <div className="job-fullscreen-body">
                  <div className="left-column">
                    <div className="job-details-section">
                      <h3>Job Details</h3>
                      <div className="job-meta-large">
                        <div className="meta-item-large">{job.employmentType}</div>
                        <div className="meta-item-large">{job.industry}</div>
                        {job.experienceLevel && <div className="meta-item-large">{job.experienceLevel} years experience</div>}
                      </div>
                    </div>
                    
                    <div className="company-metrics-section">
                      <h3>Company Ratings</h3>
                      <div className="metrics-grid-large">
                        <div className="metric-card-large">
                          <div className="metric-header-large">
                            <span className="metric-title-large">Overall Rating</span>
                            <span className="metric-score-large">{job.companyRating || '3.7'}/5</span>
                          </div>
                          <div className="metric-bar-large">
                            <div className="fill-large" style={{ width: `${((job.companyRating || 3.7) / 5) * 100}%` }}></div>
                          </div>
                        </div>
                        <div className="metric-card-large">
                          <div className="metric-header-large">
                            <span className="metric-title-large">Work-Life Balance</span>
                            <span className="metric-score-large">{job.companyMetrics?.workLife || 4.1}/5</span>
                          </div>
                          <div className="metric-bar-large">
                            <div className="fill-large" style={{ width: `${((job.companyMetrics?.workLife || 4.1) / 5) * 100}%` }}></div>
                          </div>
                        </div>
                        <div className="metric-card-large">
                          <div className="metric-header-large">
                            <span className="metric-title-large">Compensation</span>
                            <span className="metric-score-large">{job.companyMetrics?.compensation || 4.3}/5</span>
                          </div>
                          <div className="metric-bar-large">
                            <div className="fill-large" style={{ width: `${((job.companyMetrics?.compensation || 4.3) / 5) * 100}%` }}></div>
                          </div>
                        </div>
                        <div className="metric-card-large">
                          <div className="metric-header-large">
                            <span className="metric-title-large">Career Growth</span>
                            <span className="metric-score-large">{job.companyMetrics?.growth || 3.9}/5</span>
                          </div>
                          <div className="metric-bar-large">
                            <div className="fill-large" style={{ width: `${((job.companyMetrics?.growth || 3.9) / 5) * 100}%` }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="right-column">
                    <div className="salary-comparison-large">
                      <h3>Salary Comparison</h3>
                      <div className="comparison-section">
                        <h4>Regional Comparison - {job.location}</h4>
                        <div className="comparison-grid-large">
                          <div className="comparison-item-large">
                            <span className="comparison-label-large">This Position</span>
                            <span className="comparison-value-large highlight">${job.salary.toLocaleString()}</span>
                          </div>
                          <div className="comparison-item-large">
                            <span className="comparison-label-large">Average {job.title}</span>
                            <span className="comparison-value-large">${(job.salaryComparison?.average || job.salary * 0.95).toLocaleString()}</span>
                          </div>
                          <div className="comparison-item-large">
                            <span className="comparison-label-large">Entry Level</span>
                            <span className="comparison-value-large">${(job.salaryComparison?.entryLevel || job.salary * 0.7).toLocaleString()}</span>
                          </div>
                          <div className="comparison-item-large">
                            <span className="comparison-label-large">Senior Level</span>
                            <span className="comparison-value-large">${(job.salaryComparison?.seniorLevel || job.salary * 1.4).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="country-comparison-large">
                        <h4>Compare with Another Country</h4>
                        <div className="comparison-input-large">
                          <input 
                            type="text" 
                            id={`country-compare-${job.id}`} 
                            placeholder="Enter country name (e.g., Germany, Poland, UK)" 
                            className="country-input-large"
                          />
                          <button 
                            className="compare-btn-large" 
                            onClick={() => onLoginRequired?.('compare-salary')}
                          >
                            Compare
                          </button>
                        </div>
                        <div id={`country-comparison-result-${job.id}`} className="comparison-result-large"></div>
                      </div>
                    </div>
                    
                    <div className="benefits-section-large">
                      <h3>Benefits & Perks</h3>
                      <div className="benefits-grid-large">
                        {job.benefits ? (
                          job.benefits.map((benefit, idx) => (
                            <div key={idx} className={`benefit-item-large ${benefit.included ? 'included' : 'not-included'}`}>
                              {benefit.included ? '✓' : '✗'} {benefit.name}
                            </div>
                          ))
                        ) : (
                          <>
                            <div className="benefit-item-large included">✓ Health Insurance</div>
                            <div className="benefit-item-large included">✓ Retirement 401k</div>
                            <div className="benefit-item-large included">✓ Paid Time Off</div>
                            <div className="benefit-item-large included">✓ Stock Options</div>
                            <div className="benefit-item-large not-included">✗ Remote Work</div>
                            <div className="benefit-item-large included">✓ Learning Budget</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="sankey-section">
                  <h3>Income Breakdown</h3>
                  <div className="income-breakdown-compact">
                    {(() => {
                      const grossSalary = job.salary;
                      const incomeTax = grossSalary * 0.22;
                      const socialSecurity = grossSalary * 0.08;
                      const netIncome = grossSalary - incomeTax - socialSecurity;
                      const monthlyNet = netIncome / 12;
                      
                      // Monthly expenses
                      const rent = job.costOfLiving ? monthlyNet * 0.35 : monthlyNet * 0.30;
                      const food = monthlyNet * 0.15;
                      const transport = monthlyNet * 0.10;
                      const utilities = monthlyNet * 0.08;
                      const entertainment = monthlyNet * 0.10;
                      const savings = monthlyNet * 0.22;
                      
                      return (
                        <>
                          <div className="income-flow-section">
                            <h4>Annual Income Flow</h4>
                            <div className="income-flow-grid">
                              <div className="income-flow-item">
                                <span className="income-icon material-icons">account_balance_wallet</span>
                                <span className="income-label">Gross Salary</span>
                                <span className="income-value">${grossSalary.toLocaleString()}</span>
                                <div className="income-bar">
                                  <div className="income-fill" style={{ width: '100%', backgroundColor: '#1f77b4' }}></div>
                                </div>
                              </div>
                              
                              <div className="income-flow-item deduction">
                                <span className="income-icon material-icons">receipt_long</span>
                                <span className="income-label">Income Tax</span>
                                <span className="income-value">-${incomeTax.toLocaleString()}</span>
                                <div className="income-bar">
                                  <div className="income-fill" style={{ width: `${(incomeTax / grossSalary) * 100}%`, backgroundColor: '#ff7f0e' }}></div>
                                </div>
                              </div>
                              
                              <div className="income-flow-item deduction">
                                <span className="income-icon material-icons">security</span>
                                <span className="income-label">Social Security</span>
                                <span className="income-value">-${socialSecurity.toLocaleString()}</span>
                                <div className="income-bar">
                                  <div className="income-fill" style={{ width: `${(socialSecurity / grossSalary) * 100}%`, backgroundColor: '#2ca02c' }}></div>
                                </div>
                              </div>
                              
                              <div className="income-flow-item net">
                                <span className="income-icon material-icons">savings</span>
                                <span className="income-label">Net Income</span>
                                <span className="income-value">${netIncome.toLocaleString()}</span>
                                <div className="income-bar">
                                  <div className="income-fill" style={{ width: `${(netIncome / grossSalary) * 100}%`, backgroundColor: '#d62728' }}></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="monthly-breakdown-section">
                            <h4>Monthly Expense Distribution</h4>
                            <svg className="expense-breakdown-chart" viewBox="0 0 800 300" preserveAspectRatio="xMidYMid meet">
                              {/* Rent */}
                              <rect x="0" y="0" rx="6" ry="6" width={(rent / monthlyNet) * 600} height="40" 
                                    fill="#9467bd" fillOpacity="1" strokeWidth="0">
                                <title>Rent: ${rent.toLocaleString()} ({((rent / monthlyNet) * 100).toFixed(0)}%)</title>
                              </rect>
                              <text x={(rent / monthlyNet) * 600 + 10} y="25" 
                                    fill="var(--text-primary, #1a1a1a)" fontSize="14" fontWeight="600">
                                Rent: ${rent.toLocaleString()} ({((rent / monthlyNet) * 100).toFixed(0)}%)
                              </text>
                              
                              {/* Food */}
                              <rect x="0" y="50" rx="6" ry="6" width={(food / monthlyNet) * 600} height="40" 
                                    fill="#8c564b" fillOpacity="1" strokeWidth="0">
                                <title>Food: ${food.toLocaleString()} ({((food / monthlyNet) * 100).toFixed(0)}%)</title>
                              </rect>
                              <text x={(food / monthlyNet) * 600 + 10} y="75" 
                                    fill="var(--text-primary, #1a1a1a)" fontSize="14" fontWeight="600">
                                Food: ${food.toLocaleString()} ({((food / monthlyNet) * 100).toFixed(0)}%)
                              </text>
                              
                              {/* Transport */}
                              <rect x="0" y="100" rx="6" ry="6" width={(transport / monthlyNet) * 600} height="40" 
                                    fill="#e377c2" fillOpacity="1" strokeWidth="0">
                                <title>Transport: ${transport.toLocaleString()} ({((transport / monthlyNet) * 100).toFixed(0)}%)</title>
                              </rect>
                              <text x={(transport / monthlyNet) * 600 + 10} y="125" 
                                    fill="var(--text-primary, #1a1a1a)" fontSize="14" fontWeight="600">
                                Transport: ${transport.toLocaleString()} ({((transport / monthlyNet) * 100).toFixed(0)}%)
                              </text>
                              
                              {/* Utilities */}
                              <rect x="0" y="150" rx="6" ry="6" width={(utilities / monthlyNet) * 600} height="40" 
                                    fill="#7f7f7f" fillOpacity="1" strokeWidth="0">
                                <title>Utilities: ${utilities.toLocaleString()} ({((utilities / monthlyNet) * 100).toFixed(0)}%)</title>
                              </rect>
                              <text x={(utilities / monthlyNet) * 600 + 10} y="175" 
                                    fill="var(--text-primary, #1a1a1a)" fontSize="14" fontWeight="600">
                                Utilities: ${utilities.toLocaleString()} ({((utilities / monthlyNet) * 100).toFixed(0)}%)
                              </text>
                              
                              {/* Entertainment */}
                              <rect x="0" y="200" rx="6" ry="6" width={(entertainment / monthlyNet) * 600} height="40" 
                                    fill="#bcbd22" fillOpacity="1" strokeWidth="0">
                                <title>Entertainment: ${entertainment.toLocaleString()} ({((entertainment / monthlyNet) * 100).toFixed(0)}%)</title>
                              </rect>
                              <text x={(entertainment / monthlyNet) * 600 + 10} y="225" 
                                    fill="var(--text-primary, #1a1a1a)" fontSize="14" fontWeight="600">
                                Entertainment: ${entertainment.toLocaleString()} ({((entertainment / monthlyNet) * 100).toFixed(0)}%)
                              </text>
                              
                              {/* Savings */}
                              <rect x="0" y="250" rx="6" ry="6" width={(savings / monthlyNet) * 600} height="40" 
                                    fill="#17becf" fillOpacity="1" strokeWidth="0">
                                <title>Savings: ${savings.toLocaleString()} ({((savings / monthlyNet) * 100).toFixed(0)}%)</title>
                              </rect>
                              <text x={(savings / monthlyNet) * 600 + 10} y="275" 
                                    fill="var(--text-primary, #1a1a1a)" fontSize="14" fontWeight="600">
                                Savings: ${savings.toLocaleString()} ({((savings / monthlyNet) * 100).toFixed(0)}%)
                              </text>
                            </svg>
                          </div>
                          
                          <div className="income-summary-row">
                            <div className="summary-stat">
                              <span className="stat-label">Monthly Net</span>
                              <span className="stat-value">${monthlyNet.toLocaleString()}</span>
                            </div>
                            <div className="summary-stat">
                              <span className="stat-label">Tax Rate</span>
                              <span className="stat-value">{((incomeTax + socialSecurity) / grossSalary * 100).toFixed(0)}%</span>
                            </div>
                            <div className="summary-stat">
                              <span className="stat-label">Savings Rate</span>
                              <span className="stat-value">{(savings / monthlyNet * 100).toFixed(0)}%</span>
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                
                <div className="action-buttons">
                  <button className="apply-btn primary" onClick={() => onLoginRequired?.('apply')}>
                    Apply for this Position
                  </button>
                  <button className="save-btn" onClick={() => onLoginRequired?.('save')}>
                    Save Job
                  </button>
                  <button className="share-btn" onClick={() => onLoginRequired?.('share')}>
                    Share Job
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <div className="comparison-view-overlay" onClick={onClose}>
      <div className="comparison-view-content" onClick={(e) => e.stopPropagation()}>
        <div className="comparison-header">
          <h2>Compare {type === 'country' ? 'Countries' : type === 'company' ? 'Companies' : 'Jobs'}</h2>
          <div className="comparison-header-actions">
            <span className="item-count">{items.length} items</span>
            <button className="close-btn" onClick={onClose}>
              <span className="material-icons">close</span>
            </button>
          </div>
        </div>
        
        <div className="comparison-body">
          {items.length < 2 ? (
            <div className="empty-state">
              <span className="material-icons">compare</span>
              <p>Please select at least 2 {type}s to compare</p>
            </div>
          ) : (
            <>
              {type === 'country' && renderCountryComparison()}
              {type === 'company' && renderCompanyComparison()}
              {type === 'job' && renderJobComparison()}
            </>
          )}
        </div>
        
        {items.length > 2 && (
          <div className="comparison-footer">
            <div className="scroll-hint">
              <span className="material-icons">swipe</span>
              <span>Scroll horizontally to see all items</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonView;